import { createAsyncThunk } from '@reduxjs/toolkit'
import { IProcSave } from '../../data/ProcSave'
import { RunState } from './RunStore'
import { updateLastUpdatedTime } from './sliceLastSavedTimeStore'
import { TMemCytoData } from '../../data/CytoData'
import { IBasicEntityBase } from '../../data/Common'

type TProcedureSyncState = 'pending' | 'synced'

export type TLocalProcedureSave = {
  procedureId: string
  syncState: TProcedureSyncState
  updatedAt: number
  payload: IProcSave
}

function getSavedProcedureKey(procedureId: string): string {
  return `savedProcedure:${procedureId}`
}

function getFinishedProcedureKey(procedureId: string): string {
  return `finishProcedure:${procedureId}`
}

export function saveProcedureToLocal(
  procedureId: string,
  payload: IProcSave,
  syncState: TProcedureSyncState
): void {
  const localSave: TLocalProcedureSave = {
    procedureId,
    syncState,
    updatedAt: Date.now(),
    payload
  }

  localStorage.setItem(getSavedProcedureKey(procedureId), JSON.stringify(localSave))
}

export function getSavedProcedureFromLocal(procedureId: string): TLocalProcedureSave | null {
  const key = getSavedProcedureKey(procedureId)
  const raw = localStorage.getItem(key)

  if (!raw) {
    return null
  }

  try {
    const parsed = JSON.parse(raw) as TLocalProcedureSave

    if (parsed.procedureId !== procedureId) {
      localStorage.removeItem(key)
      return null
    }

    return parsed
  } catch (e) {
    console.log(`Error parsing saved procedure ${procedureId} from localStorage: ${e}`)
    localStorage.removeItem(key)
    return null
  }
}

export function removeSavedProcedureFromLocal(procedureId: string): void {
  localStorage.removeItem(getSavedProcedureKey(procedureId))
}

export function markProcedureFinishedInLocal(procedureId: string): void {
  const localSave = getSavedProcedureFromLocal(procedureId)

  if (!localSave) {
    return
  }

  localSave.payload.header.disposition = 'finished'
  localSave.updatedAt = Date.now()

  localStorage.setItem(getSavedProcedureKey(procedureId), JSON.stringify(localSave))
}

export function setFinishedProcedureMarker(identity: IBasicEntityBase): void {
  if (!identity.rowKey) {
    return
  }

  localStorage.setItem(getFinishedProcedureKey(identity.rowKey), JSON.stringify(identity))
}

export function hasFinishedProcedureMarker(procedureId: string): boolean {
  return localStorage.getItem(getFinishedProcedureKey(procedureId)) !== null
}

export function removeFinishedProcedureMarker(procedureId: string): void {
  localStorage.removeItem(getFinishedProcedureKey(procedureId))
}

export const saveCytoAsync = createAsyncThunk(
  'cytodata/saveCyto',
  async (_dummy: void, { getState }) => {
    const procHeaderSlice = (getState() as RunState).procHeaderSlice
    const cytoSlice = (getState() as RunState).cytoSlice
    const savePayload: TMemCytoData = cytoSlice.data

    const response = await fetch(`/api/saveCyto?proc=${procHeaderSlice.data?.rowKey}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(savePayload)
    })

    if (!response.ok) {
      throw new Error(`saveCyto failed with status ${response.status}`)
    }
  }
)

export const saveProcedureAsync = createAsyncThunk(
  'procHeader/saveProcedure',
  async (_dummy: void, { getState, dispatch }) => {
    const state = getState() as RunState
    const procHeaderSlice = state.procHeaderSlice
    const procedureId = procHeaderSlice.data?.rowKey

    if (!procedureId || !procHeaderSlice.data) {
      return
    }

    const currentTime = procHeaderSlice.currentTime

    const savePayload: IProcSave = {
      header: {
        ...procHeaderSlice.data,
        currentTime,
        startTime: procHeaderSlice.startTime
      },
      data: state.timelineSlice.timelineData,
      cytoData: state.cytoSlice.data
    }

    saveProcedureToLocal(procedureId, savePayload, 'pending')

    try {
      const response = await fetch('/api/saveProcedure', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(savePayload)
      })

      if (!response.ok) {
        throw new Error(`saveProcedure failed with status ${response.status}`)
      }

      saveProcedureToLocal(procedureId, savePayload, 'synced')
      dispatch(updateLastUpdatedTime(currentTime))
    } catch (e) {
      console.log(`Error saving procedure ${procedureId}: ${e}`)
      throw e
    }
  }
)